import { useState, useEffect, useRef, useCallback } from "react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const GHOST_ICON = "https://customer-assets.emergentagent.com/job_easyapp-v1/artifacts/0y0zyv63_image.png";
const APP_URL = "/apply";

function getSessionId() {
  let id = sessionStorage.getItem("ghost_sid");
  if (!id) { id = "g-" + Math.random().toString(36).slice(2, 10); sessionStorage.setItem("ghost_sid", id); }
  return id;
}

/* ── Message Bubble ── */
function Msg({ text, from }) {
  return (
    <div className={`flex ${from === "user" ? "justify-end" : "justify-start"}`}>
      <div className={`max-w-[88%] rounded-lg px-3 py-2 text-[13px] leading-relaxed ${
        from === "user"
          ? "bg-amber-500/15 text-amber-100 border border-amber-500/20"
          : "bg-[#1a1a1a] text-slate-300 border border-slate-700/50"
      }`}>{text}</div>
    </div>
  );
}

/* ── Quick Reply Buttons ── */
function QuickButtons({ options, onSelect, primary }) {
  return (
    <div className="flex flex-wrap gap-1.5 justify-start">
      {options.map((opt) => (
        <button key={opt.label} onClick={() => onSelect(opt)}
          className={`px-3 py-1.5 rounded-md text-xs font-semibold transition-colors ${
            opt.primary || opt.label === primary
              ? "bg-amber-500 hover:bg-amber-400 text-black"
              : "bg-[#1a1a1a] hover:bg-[#252525] text-slate-300 border border-slate-700"
          }`}
          data-testid={`ghost-btn-${opt.label.toLowerCase().replace(/[^a-z0-9]/g, "-")}`}
        >{opt.label}</button>
      ))}
    </div>
  );
}

/* ── Lead Capture Form ── */
function LeadForm({ preData, onSubmit, onSkip }) {
  const [f, setF] = useState({ name: "", email: "", phone: "" });
  return (
    <div className="bg-[#1a1a1a] border border-amber-500/20 rounded-lg p-3 space-y-2">
      <p className="text-[11px] text-amber-400 font-bold uppercase tracking-wider">Contact Information</p>
      <input className="w-full h-8 px-2 rounded bg-[#0a0a0a] border border-slate-700 text-white text-xs focus:border-amber-400 focus:outline-none" value={f.name} onChange={(e) => setF(p => ({...p, name: e.target.value}))} data-testid="ghost-lead-name" />
      <input className="w-full h-8 px-2 rounded bg-[#0a0a0a] border border-slate-700 text-white text-xs focus:border-amber-400 focus:outline-none" type="email" value={f.email} onChange={(e) => setF(p => ({...p, email: e.target.value}))} data-testid="ghost-lead-email" />
      <input className="w-full h-8 px-2 rounded bg-[#0a0a0a] border border-slate-700 text-white text-xs focus:border-amber-400 focus:outline-none" type="tel" value={f.phone} onChange={(e) => setF(p => ({...p, phone: e.target.value}))} data-testid="ghost-lead-phone" />
      <div className="flex gap-2">
        <button onClick={() => { if (f.name && f.email && f.phone) onSubmit({...f, ...preData}); }} data-testid="ghost-lead-submit" className="flex-1 h-8 rounded bg-amber-500 hover:bg-amber-400 text-black text-xs font-bold">Submit</button>
        <button onClick={onSkip} className="h-8 px-3 rounded bg-[#0a0a0a] border border-slate-700 text-slate-400 text-xs hover:text-white">Skip</button>
      </div>
    </div>
  );
}

/* ── Main Widget ── */
export default function GhostWidget() {
  const [open, setOpen] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [items, setItems] = useState([]);   // { type: "msg"|"buttons"|"lead"|"typing", ... }
  const [qualData, setQualData] = useState({});
  const [freeInput, setFreeInput] = useState("");
  const [sending, setSending] = useState(false);
  const [showInput, setShowInput] = useState(false);
  const [leadCaptured, setLeadCaptured] = useState(false);
  const [exitShown, setExitShown] = useState(false);
  const endRef = useRef(null);
  const sid = useRef(getSessionId());

  // Lazy load
  useEffect(() => { const t = setTimeout(() => setLoaded(true), 2500); return () => clearTimeout(t); }, []);

  // Auto-trigger after 20 seconds of no activity
  useEffect(() => {
    if (!loaded) return;
    const t = setTimeout(() => {
      if (!open && items.length === 0) {
        setOpen(true);
        pushMsg("Looking for funding options? I can help you get started.");
        pushButtons([
          { label: "Check Options", action: "check_options" },
          { label: "How It Works", action: "how_it_works" },
          { label: "Apply Now", action: "apply_now", primary: true },
        ]);
      }
    }, 20000);
    return () => clearTimeout(t);
  }, [loaded]); // eslint-disable-line

  // Exit intent
  useEffect(() => {
    if (exitShown || !loaded) return;
    const handler = (e) => {
      if (e.clientY < 5 && !open && !exitShown) {
        setExitShown(true);
        setOpen(true);
        setItems([]);
        pushMsg("Before you go -- want to see what you qualify for? It only takes a minute.");
        pushButtons([
          { label: "Yes", action: "check_options", primary: true },
          { label: "No", action: "close" },
        ]);
      }
    };
    document.addEventListener("mouseleave", handler);
    return () => document.removeEventListener("mouseleave", handler);
  }, [loaded, open, exitShown]); // eslint-disable-line

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [items]);

  // Helpers to push items
  const pushMsg = (text, from = "assistant") => setItems(p => [...p, { type: "msg", text, from }]);
  const pushButtons = (opts) => setItems(p => [...p, { type: "buttons", options: opts }]);
  const pushLead = () => setItems(p => [...p, { type: "lead" }]);
  const pushTyping = () => setItems(p => [...p, { type: "typing" }]);
  const removeTyping = () => setItems(p => p.filter(i => i.type !== "typing"));
  const removeButtons = () => setItems(p => p.filter(i => i.type !== "buttons" && i.type !== "lead"));

  const navigate = (path) => { window.location.href = path; };

  // Handle button click
  const handleButton = useCallback((opt) => {
    removeButtons();
    pushMsg(opt.label, "user");

    switch (opt.action) {
      case "check_options":
        pushMsg("Let me ask a couple quick questions so I can point you in the right direction.");
        setTimeout(() => askFundingAmount(), 800);
        break;

      case "how_it_works":
        pushMsg("We work with a network of funding partners to match you with available options based on your profile. Most decisions are fast, and in many cases there is no hard inquiry to check options.");
        setTimeout(() => {
          pushMsg("Would you like to see what you may qualify for?");
          pushButtons([
            { label: "Yes, Check Options", action: "check_options", primary: true },
            { label: "Apply Now", action: "apply_now", primary: false },
          ]);
        }, 1000);
        break;

      case "apply_now":
        pushMsg("Perfect. This will take less than a few minutes.");
        setTimeout(() => navigate(APP_URL), 1200);
        break;

      // Funding amount
      case "amt_under_5k": case "amt_5k_10k": case "amt_10k_25k": case "amt_25k_plus":
        setQualData(p => ({...p, funding_amount: opt.label}));
        setTimeout(() => askTimeline(), 600);
        break;

      // Timeline
      case "time_immediately": case "time_1_2_weeks": case "time_exploring":
        setQualData(p => ({...p, timeline: opt.label}));
        if (opt.action === "time_exploring") {
          setTimeout(() => triggerLeadCapture(), 600);
        } else {
          setTimeout(() => askCredit(), 600);
        }
        break;

      // Credit range
      case "credit_700": case "credit_640": case "credit_580": case "credit_below_580": case "credit_not_sure":
        setQualData(p => ({...p, credit_range: opt.label}));
        setTimeout(() => showPositioning(), 600);
        break;

      // Soft close
      case "final_apply":
        pushMsg("Perfect. This will take less than a few minutes.");
        setTimeout(() => navigate(APP_URL), 1200);
        break;

      case "have_questions":
        setShowInput(true);
        pushMsg("What questions do you have? I am happy to help.");
        break;

      case "move_forward":
        pushMsg("Perfect. This will take less than a few minutes.");
        setTimeout(() => navigate(APP_URL), 1200);
        break;

      case "close":
        setOpen(false);
        break;

      default:
        break;
    }
  }, []); // eslint-disable-line

  // Step 2 questions
  const askFundingAmount = () => {
    pushMsg("About how much funding are you looking for?");
    pushButtons([
      { label: "Under $5,000", action: "amt_under_5k" },
      { label: "$5,000 - $10,000", action: "amt_5k_10k" },
      { label: "$10,000 - $25,000", action: "amt_10k_25k" },
      { label: "$25,000+", action: "amt_25k_plus" },
    ]);
  };

  const askTimeline = () => {
    pushMsg("How soon are you looking to move forward?");
    pushButtons([
      { label: "Immediately", action: "time_immediately" },
      { label: "Within 1-2 weeks", action: "time_1_2_weeks" },
      { label: "Just exploring", action: "time_exploring" },
    ]);
  };

  const askCredit = () => {
    pushMsg("Which best describes your credit range? (This will not impact your credit.)");
    pushButtons([
      { label: "700+", action: "credit_700" },
      { label: "640-699", action: "credit_640" },
      { label: "580-639", action: "credit_580" },
      { label: "Below 580", action: "credit_below_580" },
      { label: "Not sure", action: "credit_not_sure" },
    ]);
  };

  // Step 3 - Positioning
  const showPositioning = () => {
    pushMsg("Based on what you shared, there may be options available. The next step would be a quick application so we can match you with the best funding partner.");
    setTimeout(() => {
      pushMsg("Would you like me to guide you through the next step, or would you prefer to go straight to the application?");
      pushButtons([
        { label: "Apply Now", action: "final_apply", primary: true },
        { label: "Have Questions", action: "have_questions" },
      ]);
    }, 1000);
  };

  // Lead capture trigger
  const triggerLeadCapture = () => {
    if (leadCaptured) return;
    pushMsg("I can have someone follow up with options based on what you are looking for.");
    pushLead();
  };

  // Submit lead
  const submitLead = async (data) => {
    removeButtons();
    try {
      await axios.post(`${API}/ghost/lead`, {
        name: data.name, email: data.email, phone: data.phone,
        notes: `Funding: ${data.funding_amount || "N/A"} | Timeline: ${data.timeline || "N/A"} | Credit: ${data.credit_range || "N/A"}`
      });
    } catch { /* silent */ }
    setLeadCaptured(true);
    pushMsg("Thanks -- someone from our team will reach out shortly.");
    setTimeout(() => {
      pushMsg("In the meantime, would you like to move forward with an application?");
      pushButtons([
        { label: "Apply Now", action: "final_apply", primary: true },
        { label: "Not right now", action: "close" },
      ]);
    }, 1000);
  };

  // Free-text question handling (objection handling via AI)
  const GHOST_ORIGIN = "Ghost is a nod to the men and women who serve -- and those we have lost.\n\nIt represents operating with discipline, precision, and purpose, without unnecessary noise. In many ways, the strongest impact comes from what works quietly in the background, guiding things forward.\n\nThat same principle applies here -- keeping things clear, focused, and helping you take the right next step without confusion.";

  const isGhostOriginQ = (t) => {
    const l = t.toLowerCase();
    return l.includes("why") && l.includes("ghost") || l.includes("what is ghost") || l.includes("called ghost") || l.includes("why ghost");
  };

  const sendFreeText = async () => {
    const text = freeInput.trim();
    if (!text || sending) return;
    setFreeInput("");
    pushMsg(text, "user");

    // Intercept Ghost origin question
    if (isGhostOriginQ(text)) {
      setSending(true);
      pushTyping();
      await new Promise(r => setTimeout(r, 1500));
      removeTyping();
      pushMsg(GHOST_ORIGIN);
      setTimeout(() => {
        pushMsg("If you would like, I can walk you through what your options may look like.");
        pushButtons([
          { label: "Check Options", action: "check_options" },
          { label: "Apply Now", action: "apply_now", primary: true },
        ]);
      }, 2000);
      setSending(false);
      return;
    }

    setSending(true);
    pushTyping();
    try {
      const res = await axios.post(`${API}/ghost/chat`, { message: text, session_id: sid.current });
      removeTyping();
      const reply = res.data.response || "For the fastest results, I recommend starting a quick application.";
      pushMsg(reply);
      // After answering, return to CTA
      setTimeout(() => {
        pushMsg("Would you like to move forward?");
        pushButtons([
          { label: "Apply Now", action: "final_apply", primary: true },
          { label: "More Questions", action: "have_questions" },
        ]);
      }, 1200);
    } catch {
      removeTyping();
      pushMsg("For the fastest results, I recommend starting a quick application.");
    } finally { setSending(false); }
  };

  // Manual open
  const handleManualOpen = () => {
    setOpen(true);
    if (items.length === 0) {
      pushMsg("Welcome. I can help you explore funding options or get you started with an application.");
      pushButtons([
        { label: "Check Options", action: "check_options" },
        { label: "How It Works", action: "how_it_works" },
        { label: "Apply Now", action: "apply_now", primary: true },
      ]);
    }
  };

  if (!loaded) return null;

  return (
    <>
      {/* Bubble */}
      {!open && (
        <button data-testid="ghost-bubble" onClick={handleManualOpen}
          className="fixed bottom-5 right-5 z-[9999] w-14 h-14 rounded-full bg-black border border-amber-500/30 shadow-[0_0_20px_rgba(0,0,0,0.5)] flex items-center justify-center hover:shadow-[0_0_25px_rgba(212,175,55,0.2)] transition-shadow">
          <img src={GHOST_ICON} alt="" className="w-9 h-9 rounded-full object-cover" />
        </button>
      )}

      {/* Panel */}
      {open && (
        <div data-testid="ghost-panel" className="fixed bottom-5 right-5 z-[9999] w-[380px] max-w-[calc(100vw-40px)] flex flex-col rounded-xl overflow-hidden shadow-[0_0_40px_rgba(0,0,0,0.6)]" style={{ height: "520px", maxHeight: "calc(100vh - 100px)" }}>
          {/* Header */}
          <div className="bg-[#0a0a0a] px-4 py-3 flex items-center justify-between border-b border-amber-500/20 shrink-0">
            <div className="flex items-center gap-3">
              <img src={GHOST_ICON} alt="" className="w-8 h-8 rounded-full object-cover" />
              <div>
                <p className="text-sm font-bold text-white leading-tight">Ghost Assistant</p>
                <p className="text-[10px] text-slate-500">Here to help with your funding options</p>
              </div>
            </div>
            <button data-testid="ghost-close" onClick={() => setOpen(false)} className="text-slate-500 hover:text-white p-1">
              <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M18 6 6 18M6 6l12 12"/></svg>
            </button>
          </div>

          {/* Messages */}
          <div className="flex-1 overflow-y-auto bg-[#111111] px-4 py-3 space-y-2.5">
            {items.map((item, i) => {
              if (item.type === "msg") return <Msg key={i} text={item.text} from={item.from} />;
              if (item.type === "buttons") return <QuickButtons key={i} options={item.options} onSelect={handleButton} />;
              if (item.type === "lead") return <LeadForm key={i} preData={qualData} onSubmit={submitLead} onSkip={() => { removeButtons(); pushMsg("No problem. Feel free to start an application anytime."); }} />;
              if (item.type === "typing") return (
                <div key={i} className="flex justify-start">
                  <div className="bg-[#1a1a1a] border border-slate-700/50 rounded-lg px-3 py-2">
                    <div className="flex gap-1"><span className="w-1.5 h-1.5 rounded-full bg-slate-500 animate-bounce" /><span className="w-1.5 h-1.5 rounded-full bg-slate-500 animate-bounce" style={{animationDelay:"0.15s"}} /><span className="w-1.5 h-1.5 rounded-full bg-slate-500 animate-bounce" style={{animationDelay:"0.3s"}} /></div>
                  </div>
                </div>
              );
              return null;
            })}
            <div ref={endRef} />
          </div>

          {/* Input (only when free-text mode) */}
          <div className="bg-[#0a0a0a] px-3 py-2.5 border-t border-slate-800 shrink-0">
            {showInput && (
              <div className="flex items-center gap-2 mb-1.5">
                <input data-testid="ghost-input" className="flex-1 h-9 px-3 rounded-lg bg-[#1a1a1a] border border-slate-700 text-white text-sm focus:border-amber-500/50 focus:outline-none"
                  value={freeInput} onChange={(e) => setFreeInput(e.target.value)} onKeyDown={(e) => e.key === "Enter" && sendFreeText()} disabled={sending} />
                <button data-testid="ghost-send" onClick={sendFreeText} disabled={sending || !freeInput.trim()}
                  className="w-9 h-9 rounded-lg bg-amber-500 hover:bg-amber-400 disabled:opacity-30 flex items-center justify-center">
                  <svg className="w-4 h-4 text-black" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="m22 2-7 20-4-9-9-4z"/><path d="m22 2-11 11"/></svg>
                </button>
              </div>
            )}
            <p className="text-[9px] text-slate-600 text-center">Ghost Assistant -- Powered by Prestigious Companies</p>
          </div>
        </div>
      )}
    </>
  );
}
